import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from '../../interceptors/axios';
import { useDispatch } from 'react-redux';
import { setAuth, setUser } from '../../redux/authSlice';
import { useNavigate } from 'react-router-dom';

// Fixed dark navy palette — login page never changes with theme
const C = {
  bg:        'radial-gradient(ellipse 140% 90% at 10% 20%, #0b1a38 0%, #060d1e 60%)',
  accent:    '#0284c7',
  accentL:   '#38bdf8',
  accentGlow:'rgba(2,132,199,.4)',
  textP:     '#e2eaf7',
  textS:     '#7a8eb0',
  textD:     '#3d4f6e',
};

const IcoGoogle = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="currentColor" opacity=".6"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="currentColor" opacity=".7"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="currentColor" opacity=".5"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="currentColor" opacity=".55"/>
  </svg>
);

const IcoFacebook = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const IcoMicrosoft = () => (
  <svg width="16" height="16" viewBox="0 0 21 21" fill="currentColor">
    <rect x="0"  y="0"  width="10" height="10" fill="#f25022" opacity=".8"/>
    <rect x="11" y="0"  width="10" height="10" fill="#7fba00" opacity=".8"/>
    <rect x="0"  y="11" width="10" height="10" fill="#00a4ef" opacity=".8"/>
    <rect x="11" y="11" width="10" height="10" fill="#ffb900" opacity=".8"/>
  </svg>
);

// Simplified PromoVideo logo (same style as sidebar)
const PvLogo = () => (
  <Box sx={{ width: 56, height: 56, flexShrink: 0, overflow: 'hidden', borderRadius: '16px', boxShadow: `0 8px 24px ${C.accentGlow}, 0 0 0 6px rgba(2,132,199,.1)` }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="56" height="56" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="loginPvBg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1A0533"/>
          <stop offset="50%" stopColor="#2D0A6B"/>
          <stop offset="100%" stopColor="#0D1B6B"/>
        </linearGradient>
        <linearGradient id="loginPvPlay" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF3CAC"/>
          <stop offset="50%" stopColor="#FF6B3D"/>
          <stop offset="100%" stopColor="#FFD700"/>
        </linearGradient>
      </defs>
      <rect width="512" height="512" fill="url(#loginPvBg)"/>
      <circle cx="256" cy="256" r="128" fill="white" fillOpacity="0.06"/>
      <circle cx="256" cy="256" r="128" fill="none" stroke="url(#loginPvPlay)" strokeWidth="3"/>
      <polygon points="220,199 220,313 330,256" fill="url(#loginPvPlay)"/>
      <polygon points="220,199 220,234 290,231" fill="white" fillOpacity="0.15"/>
    </svg>
  </Box>
);

const inputSx = {
  display: 'block', width: '100%', boxSizing: 'border-box' as const,
  background: 'rgba(56,189,248,.05)',
  border: '1px solid rgba(56,189,248,.12)',
  borderRadius: '11px',
  color: C.textP,
  fontSize: 14, px: '15px', py: '12px',
  outline: 'none',
  transition: 'border-color .15s, box-shadow .15s',
  fontFamily: 'inherit',
  '&:focus': {
    borderColor: C.accent,
    boxShadow: `0 0 0 3px ${C.accentGlow}`,
  },
  '&::placeholder': { color: 'rgba(106,128,160,.5)' },
};

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [login, setLogin]       = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post('/auth/login', { login, password });
      localStorage.setItem('token', data.token);
      dispatch(setAuth(true));
      dispatch(setUser(data.user));
      navigate('/devices');
    } catch {
      setError(t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  const labelSx = {
    fontSize: 11.5, fontWeight: 700, color: C.textS,
    display: 'block', mb: '7px', letterSpacing: '.05em', textTransform: 'uppercase' as const,
  };

  return (
    <Box sx={{
      minHeight: 'var(--app-h)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: C.bg,
      p: 2,
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Decorative blobs */}
      <Box sx={{ position: 'absolute', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(2,132,199,.18) 0%, transparent 70%)', top: '-120px', left: '-80px', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(56,189,248,.1) 0%, transparent 70%)', bottom: '-80px', right: '-60px', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,.12) 0%, transparent 70%)', top: '50%', right: '15%', pointerEvents: 'none' }} />

      {/* Card */}
      <Box sx={{
        width: { xs: '100%', sm: 400 }, maxWidth: 400,
        position: 'relative', zIndex: 1,
        background: 'rgba(8,16,36,.72)',
        backdropFilter: 'blur(32px)',
        WebkitBackdropFilter: 'blur(32px)',
        border: '1px solid rgba(56,189,248,.2)',
        borderRadius: '22px',
        overflow: 'hidden',
        boxShadow: '0 32px 80px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.03), inset 0 1px 0 rgba(255,255,255,.06)',
      }}>

        {/* Top accent line */}
        <Box sx={{ height: 3, background: `linear-gradient(90deg, transparent 0%, ${C.accent} 30%, ${C.accentL} 60%, transparent 100%)` }} />

        <Box sx={{ p: { xs: '22px 24px 26px', sm: '26px 36px 28px' } }}>

          {/* Logo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: '20px', gap: '10px' }}>
            <PvLogo />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 800, fontSize: 20, color: C.textP, letterSpacing: '-.02em', lineHeight: 1.2 }}>
                PromoVideo
              </Typography>
              <Typography sx={{ fontSize: 11, color: C.textD, letterSpacing: '.12em', textTransform: 'uppercase', mt: '2px' }}>
                Command Center
              </Typography>
            </Box>
          </Box>

          {/* Heading */}
          <Box sx={{ textAlign: 'center', mb: '20px' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 24, color: C.textP, letterSpacing: '-.02em', mb: '5px' }}>
              Welcome back
            </Typography>
            <Typography sx={{ fontSize: 13, color: C.textS }}>
              {t('login.subtitle')}
            </Typography>
          </Box>

          {/* Error */}
          {error && (
            <Box sx={{ mb: '18px', px: '14px', py: '10px', borderRadius: '10px', background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.25)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#f87171', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, color: '#f87171' }}>{error}</Typography>
            </Box>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">

            {/* Email */}
            <Box sx={{ mb: '14px' }}>
              <Typography component="label" sx={labelSx}>{t('settings.email')}</Typography>
              <Box
                component="input"
                type="text"
                value={login}
                onChange={e => setLogin((e.target as HTMLInputElement).value)}
                placeholder={t('login.emailPlaceholder')}
                autoComplete="username"
                sx={inputSx}
              />
            </Box>

            {/* Password */}
            <Box sx={{ mb: '22px' }}>
              <Typography component="label" sx={labelSx}>{t('settings.password')}</Typography>
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="input"
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword((e.target as HTMLInputElement).value)}
                  placeholder={t('login.passwordPlaceholder')}
                  autoComplete="current-password"
                  sx={{ ...inputSx, pr: '44px' }}
                />
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  sx={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', padding: '4px', cursor: 'pointer',
                    color: 'rgba(106,128,160,.6)', display: 'flex', alignItems: 'center',
                    borderRadius: '6px',
                    '&:hover': { color: C.accentL },
                    transition: 'color .15s',
                  }}
                >
                  {showPwd ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Submit */}
            <Box
              component="button"
              type="submit"
              disabled={loading}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                width: '100%', py: '13px', borderRadius: '12px', border: 'none',
                background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentL} 100%)`,
                color: '#fff', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? .75 : 1,
                boxShadow: `0 4px 20px ${C.accentGlow}`,
                transition: 'opacity .15s, transform .1s, box-shadow .15s',
                fontFamily: 'inherit',
                '&:hover': loading ? {} : { opacity: .92, transform: 'translateY(-1px)', boxShadow: '0 8px 28px rgba(2,132,199,.55)' },
                '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 10px rgba(2,132,199,.3)' },
              }}
            >
              {loading && <CircularProgress size={15} sx={{ color: '#fff' }} />}
              {loading ? t('login.signingIn') : t('login.signIn')}
            </Box>
          </form>

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', my: '22px' }}>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(56,189,248,.1)' }} />
            <Typography sx={{ fontSize: 11, color: 'rgba(106,128,160,.55)', letterSpacing: '.06em', textTransform: 'uppercase', flexShrink: 0 }}>
              or continue with
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(56,189,248,.1)' }} />
          </Box>

          {/* Social icons — disabled */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
            {[
              { label: 'Google', icon: <IcoGoogle /> },
              { label: 'Microsoft', icon: <IcoMicrosoft /> },
              { label: 'Facebook', icon: <IcoFacebook /> },
            ].map(btn => (
              <Box
                key={btn.label}
                component="button"
                disabled
                title={`${btn.label} — coming soon`}
                sx={{
                  width: 46, height: 46,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '11px',
                  background: 'rgba(255,255,255,.04)',
                  border: '1px solid rgba(255,255,255,.08)',
                  color: 'rgba(160,180,210,.5)',
                  cursor: 'not-allowed',
                  filter: 'grayscale(1)',
                  opacity: 0.5,
                }}
              >
                {btn.icon}
              </Box>
            ))}
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default Login;
